import { FAST_API_URL, postOptions } from "../index";

// ── Types ──────────────────────────────────────────────

interface HealthCheckParams {
    mediaId: string;
    folderNum: number;
    lang: string;
    language_from?: string;
}

interface DiagnoseParams extends HealthCheckParams {
    subtitle_ids?: string[] | null;
}

interface DiagnoseFixParams extends HealthCheckParams {
    fix_ids: string[];
}

export interface HealthCheckSummary {
    total_issues: number;
    total_fixed: number;
    total_flagged: number;
}

export interface PrepareFolderResponse {
    phases: {
        russian: Record<string, any>;
        translation: Record<string, any>;
    };
    summary: HealthCheckSummary;
    error?: string;
}

export interface R6PreviewItem {
    text: string;
    start_ms: number;
    end_ms: number;
    old_text: string;
    action: "update" | "merge" | "new" | "keep";
}

export interface DiagnoseIssue {
    fix_id: string;
    type: string;
    side: "russian" | "translation";
    subtitle_id: string;
    description: string;
    auto_fixable: boolean;
    start_ms?: number;
    old_text?: string;
    new_text?: string;
    _preview?: R6PreviewItem[];
}

export interface DiagnoseFolderResponse {
    issues: DiagnoseIssue[];
    summary: {
        total: number;
        auto_fixable: number;
        flagged_only: number;
        passes?: number;
        strategies?: string;
    };
    error?: string;
}

export interface StepLogEntry {
    step: string;
    result: string;
}

export interface SourceHealthCheckResponse extends DiagnoseFolderResponse {
    steps_log?: StepLogEntry[];
    auto_fixed?: {
        r3_overlaps: number;
        r1_merged: number;
        r4_spelling: number;
        r7_gaps_filled: number;
        r8_sentences: number;
        r9_splits: number;
        r10_aligned: number;
        aligned: number;
    };
    auto_fixed_total?: number;
    completed_steps?: string[];
}

export interface DiagnoseFixResponse {
    fixed: { fix_id: string; result: string }[];
    failed: { fix_id: string; error: string }[];
}

// ── API Functions ──────────────────────────────────────

export const prepareFolderApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
}: HealthCheckParams): Promise<PrepareFolderResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/folder_prepare?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || "ru",
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to prepare folder");
    }

    return await response.json();
};

export const diagnoseFolderApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    subtitle_ids,
}: DiagnoseParams): Promise<DiagnoseFolderResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/folder_diagnose?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || "ru",
                subtitle_ids: subtitle_ids || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to diagnose folder");
    }

    return await response.json();
};

export interface RebuildVoiceoversResponse {
    steps: Record<string, any>;
    summary: string;
    error?: string;
}

export const rebuildVoiceoversApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    speed,
}: HealthCheckParams & { speed?: number }): Promise<RebuildVoiceoversResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/folder_rebuild_voiceovers?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || "ru",
                // Optional manual speed override (blank in UI = auto-compute).
                ...(speed != null ? { speed } : {}),
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to rebuild voiceovers");
    }

    return await response.json();
};

export const sourceHealthCheckApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    subtitle_ids,
    step,
}: DiagnoseParams & { step?: string }): Promise<SourceHealthCheckResponse> => {
    const payload: Record<string, any> = {
        folder_num: folderNum,
        lang: lang,
        language_from: language_from || "ru",
        subtitle_ids: subtitle_ids || null,
    };
    if (step) payload.step = step;

    const response = await fetch(
        `${FAST_API_URL}/source_health_check?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(payload),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to run source health check");
    }

    return await response.json();
};

export interface FullRebuildResponse {
    created: number;
    steps: Record<string, { detected: number; applied: number; failed: number }>;
    summary: string;
    error?: string;
}

export const sourceFullRebuildApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    steps,
}: HealthCheckParams & { steps?: string[] }): Promise<FullRebuildResponse> => {
    const payload: Record<string, any> = {
        folder_num: folderNum,
        lang: lang,
        language_from: language_from || "ru",
    };
    if (steps) payload.steps = steps;

    const response = await fetch(
        `${FAST_API_URL}/source_full_rebuild?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(payload),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to run full rebuild");
    }

    return await response.json();
};

export const applyDiagnoseFixesApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    fix_ids,
}: DiagnoseFixParams): Promise<DiagnoseFixResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/folder_diagnose_fix?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || "ru",
                fix_ids: fix_ids,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to apply fixes");
    }

    return await response.json();
};
